import * as THREE from "three";
import { LineSegments2 } from "three/addons/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/addons/lines/LineSegmentsGeometry.js";
import type { LineMaterial } from "three/addons/lines/LineMaterial.js";

// Hand-drawn wobble: each geometric edge is subdivided and its interior points
// pushed along a smooth low-frequency wave (perpendicular to the edge), tapered
// to zero at the corners so boxes still close. Seeded per-edge from its
// endpoints → deterministic (stable across reloads), baked once (no per-frame
// cost). White polygon-offset fills still occlude the back edges.

const AMP = 0.06; // max wobble in world units
const SEG_LEN = 0.35; // target sub-segment length
const MAX_SEGS = 16;

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _p1 = new THREE.Vector3();
const _p2 = new THREE.Vector3();
const _pt = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _alt = new THREE.Vector3(1, 0, 0);

// deterministic pseudo-random in [0,1) from a scalar seed
function rand(seed: number): number {
  const s = Math.sin(seed * 127.1) * 43758.5453;
  return s - Math.floor(s);
}

export function addEdges(
  mesh: THREE.Mesh,
  material: LineMaterial,
  thresholdDeg = 30,
): LineSegments2 {
  const edges = new THREE.EdgesGeometry(mesh.geometry, thresholdDeg);
  const pos = edges.attributes.position;
  const out: number[] = [];

  for (let e = 0; e < pos.count; e += 2) {
    _a.fromBufferAttribute(pos, e);
    _b.fromBufferAttribute(pos, e + 1);
    _dir.subVectors(_b, _a);
    const len = _dir.length();
    if (len < 1e-5) continue;
    _dir.divideScalar(len);

    // orthonormal basis perpendicular to the edge
    _p1.crossVectors(_dir, Math.abs(_dir.y) > 0.9 ? _alt : _up).normalize();
    _p2.crossVectors(_dir, _p1).normalize();

    const segs = Math.min(MAX_SEGS, Math.max(2, Math.round(len / SEG_LEN)));
    const amp = Math.min(AMP, len * 0.15); // don't over-wobble tiny edges
    const seed = (_a.x + _b.x) * 12.99 + (_a.y + _b.y) * 78.23 +
      (_a.z + _b.z) * 37.72;
    const ph1 = rand(seed) * Math.PI * 2;
    const ph2 = rand(seed + 1.3) * Math.PI * 2;
    const f1 = 1.5 + rand(seed + 2.7) * 2;
    const f2 = 1.5 + rand(seed + 3.9) * 2;

    let px = 0, py = 0, pz = 0;
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      _pt.lerpVectors(_a, _b, t);
      const env = Math.sin(Math.PI * t); // taper to 0 at both ends
      _pt.addScaledVector(_p1, amp * env * Math.sin(t * Math.PI * f1 + ph1));
      _pt.addScaledVector(_p2, amp * env * Math.sin(t * Math.PI * f2 + ph2));
      if (i > 0) out.push(px, py, pz, _pt.x, _pt.y, _pt.z);
      px = _pt.x;
      py = _pt.y;
      pz = _pt.z;
    }
  }
  edges.dispose();

  const geometry = new LineSegmentsGeometry();
  geometry.setPositions(out);
  const seg = new LineSegments2(geometry, material);
  seg.userData.isEdge = true;
  mesh.add(seg);
  return seg;
}
